export function coerceJsonArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Fill missing venue / machine list fields from get_job_with_images or sparse schedule rows.
 * Some payloads omit service_venue_id but include venue_machines[].venue_id.
 */
export function normalizeJobRecord(job) {
  if (!job || typeof job !== "object") return job;
  const venue_machines = coerceJsonArray(job.venue_machines);
  const machine_ids_array = coerceJsonArray(job.machine_ids_array);
  const inferredVenue =
    job.service_venue_id ??
    job.venue_id ??
    job.venue_id_fk ??
    venue_machines[0]?.venue_id ??
    venue_machines[0]?.venue_id_fk;
  const hasVenue =
    inferredVenue != null && String(inferredVenue).trim() !== "";
  return {
    ...job,
    venue_machines,
    machine_ids_array,
    ...(hasVenue
      ? {
          service_venue_id: inferredVenue,
          venue_id: job.venue_id ?? inferredVenue,
        }
      : {}),
  };
}

/**
 * Normalize rows from job payloads (e.g. venue_machines) to match get_machines / UI shape.
 */
export function normalizeVenueMachine(vm) {
  if (!vm || vm.machine_id_pk == null) return null;
  const loc = vm.location_id ?? vm.venue_location_id ?? 0;
  return {
    ...vm,
    venue_id: vm.venue_id ?? vm.venue_id_fk,
    location_id: loc,
    venue_location_id: vm.venue_location_id ?? loc,
    venue_location_id_fk: vm.venue_location_id_fk ?? vm.venue_location_id ?? loc,
  };
}

export function resolveMachineById(machineId, machines, venueMachines) {
  const id = String(machineId);
  const fromContext = (machines || []).find(
    (m) => String(m.machine_id_pk) === id
  );
  if (fromContext) return fromContext;
  const vmList = coerceJsonArray(venueMachines);
  const raw = vmList.find((m) => String(m.machine_id_pk) === id);
  return normalizeVenueMachine(raw);
}

/** Venue id for the call (schedule API may use venue_id; detail payloads use service_venue_id). */
export function getJobVenueId(job) {
  if (!job) return null;
  const rows = coerceJsonArray(job.venue_machines);
  const v =
    job.service_venue_id ??
    job.venue_id ??
    job.venue_id_fk ??
    job.service_venue_id_fk;
  if (v != null && String(v).trim() !== "") return String(v);
  const fromRow = rows[0]?.venue_id ?? rows[0]?.venue_id_fk;
  if (fromRow != null && String(fromRow).trim() !== "") return String(fromRow);
  return null;
}

/**
 * Machines tied to this call's venue: same venue_id as the job venue, on the call's
 * machine_ids_array, or listed on job.venue_machines. Use for Remove/Swap when global
 * get_machines has stale venue_id on rows that still match by id on the schedule.
 */
export function getMachinesAtVenueForJob(job, machines) {
  if (!job) return [];
  const venueId = getJobVenueId(job);
  const idsOnCall = new Set(coerceJsonArray(job.machine_ids_array).map(String));
  const merged = new Map();
  const payload = coerceJsonArray(job.venue_machines);

  for (const m of machines || []) {
    const id = String(m.machine_id_pk);
    if (!id || id === "undefined") continue;
    const atVenue = venueId != null && String(m.venue_id) === venueId;
    const onCall = idsOnCall.has(id);
    if (atVenue || onCall) merged.set(id, m);
  }
  for (const vm of payload) {
    const row = normalizeVenueMachine(vm);
    if (!row) continue;
    const id = String(row.machine_id_pk);
    const onCall = idsOnCall.has(id);
    const atVenue = venueId == null || String(row.venue_id) === venueId;
    if (!onCall && !atVenue) continue;
    const existing = merged.get(id);
    if (!existing || (existing && !existing.serial_id && row.serial_id)) {
      merged.set(id, row);
    }
  }
  return [...merged.values()];
}
